import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';
import { shopV, type XmShop } from '../objectsXimen';

/**
 * Ximending (西門町), Taipei's youth shopping district, seen with north up:
 *
 *   Cinema Park ▒ cinemas · bubble tea · arcade · claw shop        ║ Zhonghua
 *   ═══════════ 武昌街 Cinema Street ════════════════════════════  ║ Road
 *   rooftops ▒ tattoo · nails · street wear ║ mee sua · chicken · ice · photo stickers ║ KTV
 *   ═══════════ 峨眉街 Emei Street ═══ busker square ════════════  ║ (traffic)
 *   rooftops ▒ books · fashion · 萬象 screen ║ ramen · electronics · anime ║ mart · bank
 *   ─ frontage ─ MRT Exit 6 plaza (meeting point) ──────────────────
 *   ═══════════ 成都路 Chengdu Road ═══ rainbow crossing ════════════
 *               西門紅樓 Red House (octagon + cross hall), weekend market on the plaza
 *
 * Hanzhong Street (漢中街) runs north–south through the middle; Kunming Street on the west and
 * a lane on the east connect the blocks. Ground: '4' painted pedestrian street, '5' Red House
 * brick plaza, '6' rooftops (and Zhonghua Road, which you can't walk onto).
 */
const W = 88, H = 60;
const b = new MapBuilder(W, H, '6');

// Cinema Park in the north-west corner (graffiti wall along the top).
b.fill(0, 2, 21, 6, 's').fill(2, 3, 6, 3, 'g').fill(14, 3, 5, 3, 'g');
// Pedestrian streets.
b.fill(0, 8, 74, 4, '4'); // 武昌街 Cinema Street
b.fill(0, 21, 74, 4, '4'); // 峨眉街 Emei Street
b.fill(30, 21, 13, 7, '4'); // busker square at Emei × Hanzhong
b.fill(34, 8, 5, 30, '4'); // 漢中街 Hanzhong Street
b.fill(4, 8, 4, 30, '4'); // 昆明街 Kunming Street
b.fill(60, 8, 4, 30, '4'); // east lane
b.fill(0, 34, 74, 4, '4'); // frontage along Chengdu Road
b.fill(22, 34, 12, 4, 'X'); // MRT Exit 6 plaza
b.fill(0, 38, 77, 2, 's').fill(74, 0, 3, 40, 's');
// 成都路 Chengdu Road and its south pavement.
b.fill(0, 40, 77, 5, 'r').fill(0, 42, 77, 1, 'l');
b.fill(0, 45, 77, 1, 's');
// Red House grounds.
b.fill(0, 46, 77, 13, 'g').fill(14, 46, 47, 13, '5').fill(0, 52, 14, 2, 'p').fill(61, 52, 16, 2, 'p');
b.fill(0, 59, 77, 1, 'h');
b.fill(2, 55, 10, 3, 'G').fill(64, 55, 10, 3, 'G');

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const tree = (tx: number, ty: number, kind: 'tree' | 'cherry' = 'tree'): Decor => ({ kind, ...at(tx, ty), solid: { x: px(tx) - 6, y: px(ty) - 6, w: 12, h: 6 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', ...at(tx, ty), solid: { x: px(tx) - 2, y: px(ty) - 3, w: 4, h: 3 } });
const decor: Decor[] = [];
const lights: Light[] = [];
const NEON: Record<string, string> = { r: '255,90,90', p: '255,90,200', c: '90,220,255', y: '255,220,120', g: '120,255,160', v: '180,120,255', w: '255,240,210' };

/** A row of shop facades along the south side of a block: [shop, width, neon colour, vertical sign?]. */
function facades(base: number, x0: number, list: [XmShop, number, keyof typeof NEON, number?][], h = 64): void {
  let x = x0;
  for (const [k, w, col, vs] of list) {
    decor.push({ kind: 'xm_shop', x: px(x) + px(w) / 2, y: px(base), w: px(w), h, variant: shopV(k) });
    lights.push({ x: px(x) + px(w) / 2, y: px(base) - 20, r: 30 + w * 6, color: NEON[col] });
    if (vs !== undefined) {
      const sx = px(x) + (w > 4 ? 10 : 6);
      decor.push({ kind: 'xm_vsign', x: sx, y: px(base) + 1, variant: vs });
      lights.push({ x: sx, y: px(base) - 54, r: 34, color: NEON[col] });
    }
    x += w;
  }
}

// Block A — Cinema Street's north side (cinemas, tea, arcade).
facades(8, 21, [
  ['cinemaStar', 11, 'y', 0], ['records', 4, 'v'], ['sneaker', 5, 'c', 1], ['cinemaCentury', 11, 'r', 0],
  ['pearlTea', 4, 'y', 2], ['arcade', 7, 'p', 3], ['claw', 5, 'p', 4], ['cinemaDaily', 6, 'y'],
]);
// Block B — Emei Street's north side.
facades(21, 8, [['tattoo', 4, 'r', 5], ['nails', 4, 'p', 6], ['streetwear', 6, 'c'], ['drugstore', 6, 'g', 7], ['accessories', 3, 'p'], ['phonecase', 3, 'c']]);
facades(21, 39, [['meesua', 5, 'y'], ['chicken', 4, 'y', 9], ['ice', 5, 'c', 10], ['shoes', 3, 'w'], ['sticker', 4, 'p', 11]]);
facades(21, 64, [['ktv', 6, 'v', 12], ['hotel', 4, 'r', 13]]);
// Block C — the side facing Chengdu Road.
facades(34, 8, [['books', 4, 'w', 17], ['glasses', 4, 'c', 14], ['fashion', 6, 'p']]);
facades(34, 22, [['wanxiang', 8, 'c']], 72);
facades(34, 30, [['coffee', 4, 'y']]);
facades(34, 39, [['ramen', 4, 'r', 16], ['electronics', 5, 'c', 19], ['sports', 6, 'g'], ['anime', 6, 'p', 15]]);
facades(34, 64, [['mart', 5, 'w'], ['bank', 5, 'w']]);
// The two corner buildings at Hanzhong × Chengdu get a big "西門町" sign each.
decor.push({ kind: 'xm_vsign', x: px(33) + 8, y: px(34) + 1, variant: 18 }, { kind: 'xm_vsign', x: px(39) + 6, y: px(21) + 1, variant: 18 });
lights.push({ x: px(33) + 8, y: px(34) - 54, r: 40, color: NEON.p }, { x: px(39) + 6, y: px(21) - 54, r: 40, color: NEON.p });

// Giant video screens: on the 萬象 building over Exit 6, and over the busker square.
decor.push({ kind: 'xm_screen', x: px(26), y: px(34), w: px(7), variant: 0 });
decor.push({ kind: 'xm_screen', x: px(14), y: px(21), w: px(5), variant: 1 });
decor.push({ kind: 'xm_screen', x: px(56), y: px(34), w: px(5), variant: 2 });
lights.push({ x: px(26), y: px(34) - 84, r: 90, color: '140,200,255' }, { x: px(14), y: px(21) - 80, r: 70, color: '255,140,220' }, { x: px(56), y: px(34) - 80, r: 70, color: '160,255,200' });

// Rooftop billboards over the blocks (Ximending's skyline is all signs).
for (const [tx, ty, v] of [[24, 15, 0], [47, 15, 3], [69, 15, 5], [12, 29, 1], [44, 29, 4], [68, 29, 2], [35, 3, 2], [59, 3, 0]] as const) {
  decor.push({ kind: 'xm_billboard', x: px(tx), y: px(ty), w: 76, variant: v });
  lights.push({ x: px(tx), y: px(ty) - 30, r: 50, color: '255,250,220' });
}
// Cinema Park: the graffiti wall, a skate ramp, a film-reel sculpture, trees and benches.
decor.push({ kind: 'xm_graffiti', x: px(10) + 8, y: px(2), w: px(21) });
decor.push({ kind: 'xm_ramp', x: px(11), y: px(7), w: 48, solid: { x: px(9) + 8, y: px(6), w: 48, h: 16 } });
decor.push({ kind: 'xm_reel', x: px(17), y: px(6) + 8, solid: { x: px(16) + 6, y: px(6), w: 20, h: 8 } });
decor.push(tree(3, 4), tree(6, 5), tree(19, 4), tree(15, 5, 'cherry'));
decor.push({ kind: 'xm_bench', x: px(4), y: px(7), solid: { x: px(3), y: px(7) - 6, w: 32, h: 6 } });

// Street furniture on the pedestrian streets.
for (const tx of [10, 20, 30, 44, 56, 70]) decor.push(lamp(tx, 12));
for (const tx of [2, 12, 26, 48, 58, 70]) decor.push(lamp(tx, 25));
for (const tx of [3, 15, 42, 52, 66, 72]) decor.push(lamp(tx, 38));
for (const l of decor.filter((d) => d.kind === 'lamp')) lights.push({ x: l.x, y: l.y - 26, r: 56, color: '255,224,150' });
// Ground plaques and painted art (flat decor, anchored at their top edge).
for (const [tx, ty, v] of [[36, 12, 0], [36, 30, 1], [6, 10, 1], [61, 22, 0], [16, 35, 0], [68, 9, 1], [47, 35, 1]]) decor.push({ kind: 'xm_plaque', x: px(tx) + 8, y: px(ty), variant: v });
// Vending machines, bins, planters, a phone-charging kiosk, sale A-boards and a balloon man's cart.
decor.push(
  { kind: 'vendingDeco', x: px(32) + 8, y: px(21) + 2, solid: { x: px(32), y: px(21) - 2, w: 16, h: 4 } },
  { kind: 'trashBin', ...at(43, 25), solid: { x: px(43) - 4, y: px(25) - 4, w: 8, h: 4 } },
  { kind: 'trashBin', ...at(23, 12), solid: { x: px(23) - 4, y: px(12) - 4, w: 8, h: 4 } },
  { kind: 'trashBin', ...at(59, 38), solid: { x: px(59) - 4, y: px(38) - 4, w: 8, h: 4 } },
  { kind: 'planter', ...at(28, 25), solid: { x: px(27) + 4, y: px(25) - 8, w: 24, h: 8 } },
  { kind: 'planter', ...at(46, 25), solid: { x: px(45) + 4, y: px(25) - 8, w: 24, h: 8 } },
  { kind: 'xm_aboard', ...at(13, 23), variant: 0 }, { kind: 'xm_aboard', ...at(30, 10), variant: 1 }, { kind: 'xm_aboard', ...at(66, 23), variant: 2 },
  { kind: 'xm_aboard', ...at(57, 11), variant: 1 }, { kind: 'xm_aboard', ...at(48, 37), variant: 0 },
  { kind: 'xm_balloons', x: px(40) + 8, y: px(27), solid: { x: px(40), y: px(27) - 6, w: 16, h: 6 } },
  { kind: 'xm_streetSign', x: px(33) + 8, y: px(12), variant: 0, solid: { x: px(33) + 6, y: px(12) - 3, w: 4, h: 3 } },
  { kind: 'xm_streetSign', x: px(39) + 4, y: px(25) + 2, variant: 1, solid: { x: px(39) + 2, y: px(25) - 1, w: 4, h: 3 } },
  { kind: 'xm_streetSign', x: px(8) + 4, y: px(12), variant: 2, solid: { x: px(8) + 2, y: px(12) - 3, w: 4, h: 3 } },
);
// The busker square: a ring of spectators (left open towards the south).
const ring: [number, number, number][] = [
  [-34, -10, 3], [-28, -22, 3], [-14, -28, 0], [0, -30, 0], [14, -28, 0], [28, -22, 2], [34, -10, 2], [-38, 4, 3], [38, 4, 2],
];
ring.forEach(([dx, dy, f], i) => {
  const x = px(36) + 8 + dx, y = px(25) + dy + 8;
  decor.push({ kind: 'xm_person', x, y, variant: ((i * 5 + 3) % 16) * 4 + f, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
});
// Crowds: standing mee-sua eaters, people waiting at Exit 6, shoppers looking in windows.
const crowd: [number, number, number, number][] = [
  [40.2, 22.6, 1, 0], [42.9, 22.8, 4, 0], [44, 23.4, 7, 2], [39, 23.2, 9, 3],
  [27.2, 37.6, 2, 0], [28.4, 38.4, 6, 3], [34.4, 36.9, 10, 0], [33.4, 38.6, 12, 2], [25.6, 38.4, 14, 1],
  [12.5, 21.9, 5, 1], [20.4, 22.1, 8, 1], [58, 9.6, 11, 1], [26.5, 10.4, 13, 1], [54.5, 35.2, 15, 1], [67, 22.2, 3, 1],
];
for (const [tx, ty, look, f] of crowd) {
  const x = px(tx), y = px(ty);
  decor.push({ kind: 'xm_person', x, y, variant: look * 4 + f, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
}
// Standing counters where people eat their mee sua.
decor.push({ kind: 'xm_standTable', x: px(41) + 8, y: px(23) + 4, solid: { x: px(41), y: px(23) - 2, w: 16, h: 6 } });
// Parked scooters: along the lanes and the east pavement (Zhonghua Road side).
const scooters = (tx: number, ty: number, n: number, dy = 0.75) => {
  for (let i = 0; i < n; i++) decor.push({ kind: 'scooter', x: px(tx) + 8, y: px(ty) + i * dy * 16 + 10, variant: (i * 7 + tx) % 3 ? 0 : 1, solid: { x: px(tx) + 1, y: px(ty) + i * dy * 16 + 4, w: 14, h: 6 } });
};
scooters(75, 2, 8);
scooters(75, 13, 9);
scooters(75, 27, 9);
scooters(4, 13, 7);
scooters(63, 26, 7);
decor.push({ kind: 'xm_ubike', x: px(18), y: px(39) + 4, w: px(4), solid: { x: px(16), y: px(39) - 4, w: px(4), h: 8 } });
// Zhonghua Road and Chengdu Road: traffic, kerb railings, the rainbow crossing and lights.
// Zhonghua Road in 5-row bands (decor is culled by its anchor), Chengdu Road drawn from its top edge.
for (let ty = 0; ty < H; ty += 5) decor.push({ kind: 'xm_traffic', x: px(81) + 8, y: px(ty + 5), w: px(9), h: px(5), variant: 0 });
decor.push({ kind: 'xm_traffic', x: px(38), y: px(40) + 1, w: px(77), h: px(5), variant: 1 });
decor.push({ kind: 'xm_rainbow', x: px(36) + 8, y: px(40), w: px(5), h: px(5) });
const rail = (x0: number, x1: number, ty: number): Decor => ({ kind: 'xm_rail', x: (px(x0) + px(x1)) / 2, y: px(ty) + 2, w: px(x1 - x0), solid: { x: px(x0), y: px(ty) - 1, w: px(x1 - x0), h: 4 } });
decor.push(rail(0, 34, 40), rail(39, 77, 40), rail(0, 34, 45), rail(39, 77, 45));
decor.push({ kind: 'trafficLight', ...at(33, 40) }, { kind: 'trafficLight', ...at(39, 46) });
decor.push({ kind: 'xm_busStop', x: px(52), y: px(39) + 6, solid: { x: px(50), y: px(39) - 2, w: px(4), h: 6 } });
for (const tx of [6, 46, 62, 70]) decor.push(tree(tx, 39));
// The Red House: octagon + cross hall, lamps and trees around its plaza, the weekend market.
decor.push({ kind: 'xm_redHouse', x: px(45), y: px(52), w: px(10), solid: { x: px(40), y: px(46), w: px(10), h: px(6) } });
decor.push({ kind: 'xm_crossHall', x: px(32), y: px(52), w: px(16), solid: { x: px(24), y: px(47), w: px(16), h: px(5) } });
decor.push({ kind: 'xm_redSign', x: px(56), y: px(54), solid: { x: px(54) + 4, y: px(54) - 4, w: px(4) - 8, h: 4 } });
for (const tx of [16, 22, 52, 58]) decor.push(lamp(tx, 58));
for (const [tx, ty] of [[2, 48], [7, 47], [11, 49], [4, 51], [64, 48], [69, 47], [74, 49], [72, 51], [62, 57], [13, 57]]) decor.push(tree(tx, ty));
decor.push({ kind: 'xm_bench', x: px(20), y: px(58), solid: { x: px(19), y: px(58) - 6, w: 32, h: 6 } }, { kind: 'xm_bench', x: px(48), y: px(58), solid: { x: px(47), y: px(58) - 6, w: 32, h: 6 } });
decor.push({ kind: 'bikeRack', x: px(66), y: px(53) + 12 });
for (const [tx, ty, v] of [[17, 49, 0], [20, 50, 1], [17, 52, 2], [55, 49, 1], [58, 50, 0], [57, 53, 2]]) decor.push({ kind: 'xm_cafe', x: px(tx), y: px(ty), variant: v, solid: { x: px(tx) - 10, y: px(ty) - 6, w: 20, h: 6 } });
for (const [tx, ty] of [[23, 56], [41, 57], [51, 56]]) decor.push({ kind: 'planter', ...at(tx, ty), solid: { x: px(tx) - 12, y: px(ty) - 8, w: 24, h: 8 } });
for (const [tx, ty, look, f] of [[18.4, 50.6, 3, 0], [56.2, 50.4, 9, 0], [38, 54, 12, 1], [44, 55.2, 6, 1], [33, 57.6, 14, 2]]) decor.push({ kind: 'xm_person', x: px(tx), y: px(ty), variant: look * 4 + f, solid: { x: px(tx) - 4, y: px(ty) - 3, w: 8, h: 3 } });

for (const l of decor.filter((d) => d.kind === 'lamp' && d.y > px(50))) lights.push({ x: l.x, y: l.y - 26, r: 56, color: '255,224,150' });
lights.push(
  // Exit 6 canopy, the Red House floodlights, the market fairy lights, the screens' spill on the street.
  { x: px(31), y: px(35), r: 60, color: '200,230,255' },
  { x: px(45), y: px(50), r: 110, color: '255,170,110' },
  { x: px(32), y: px(50), r: 100, color: '255,170,110' },
  { x: px(33), y: px(56), r: 70, color: '255,220,150' },
  { x: px(36), y: px(25), r: 80, color: '255,200,150' },
  { x: px(81), y: px(20), r: 60, color: '255,240,200' },
  { x: px(81), y: px(48), r: 60, color: '255,240,200' },
);

export const ximen: MapDef = {
  id: 'ximen',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    { key: 'xmCinemaPark', rect: { x: 0, y: 0, w: px(21), h: px(8) } },
    { key: 'xmCinemaStreet', rect: { x: 0, y: px(8), w: px(74), h: px(4) } },
    { key: 'xmSquare', rect: { x: px(30), y: px(21), w: px(13), h: px(7) } },
    { key: 'xmEmei', rect: { x: 0, y: px(21), w: px(74), h: px(4) } },
    { key: 'xmExit6', rect: { x: px(20), y: px(33), w: px(16), h: px(7) } },
    { key: 'xmRedHouse', rect: { x: 0, y: px(45), w: px(77), h: px(15) } },
    { key: 'xmChengdu', rect: { x: 0, y: px(38), w: px(77), h: px(7) } },
    { key: 'xmZhonghua', rect: { x: px(74), y: 0, w: px(14), h: px(H) } },
    { key: 'ximen', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: { mrt: { x: px(31), y: px(37) + 8, facing: 'down' } },
};
